'use client';

import { token } from '@terra/ui-styled-system';
import { Badge, type BadgeProps } from '../badge';

export type BadgeSeverityType = 'Critical' | 'High' | 'Medium' | 'Low' | 'Info';

export type BadgeSeverityProps = BadgeProps & {
  severity: BadgeSeverityType;
};

export type BadgeSeverityConfig = {
  bg: string;
  borderColor: string;
  fg?: string;
};

export const BADGE_SEVERITY_VALUES = [
  'Critical',
  'High',
  'Medium',
  'Low',
  'Info',
] as const satisfies readonly BadgeSeverityType[];

export const BADGE_SEVERITY_CONFIG: Record<
  Exclude<BadgeSeverityType, 'Info'>,
  BadgeSeverityConfig
> = {
  Critical: {
    bg: token('colors.bg.severity.critical'),
    borderColor: token('colors.border.severity.critical'),
  },
  High: {
    bg: token('colors.bg.severity.high'),
    borderColor: token('colors.border.severity.high'),
  },
  Medium: {
    bg: token('colors.bg.severity.medium'),
    borderColor: token('colors.border.severity.medium'),
  },
  Low: {
    bg: token('colors.bg.severity.low'),
    borderColor: token('colors.border.severity.low'),
  },
};

export const isBadgeSeverityType = (
  value: unknown,
): value is BadgeSeverityType =>
  typeof value === 'string' &&
  (BADGE_SEVERITY_VALUES as readonly string[]).includes(value);

export const BadgeSeverity = ({
  severity,
  variant = 'emphasis',
  size = 'md',
  children,
  ...props
}: BadgeSeverityProps) => {
  const config = severity !== 'Info' ? BADGE_SEVERITY_CONFIG[severity] : null;

  return (
    <Badge
      variant={variant}
      size={size}
      bg={config?.bg}
      borderColor={config?.borderColor}
      color={config?.fg}
      {...props}
    >
      {children}
    </Badge>
  );
};
